'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';

const PlatformsSection = () => {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            We'll List Your Property on
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Maximize your property's visibility and bookings by listing on the world's most popular travel and accommodation platforms
          </p>
        </motion.div>

        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden shadow-2xl">
            <Image
              src="https://images.pexels.com/photos/271624/pexels-photo-271624.jpeg"
              alt="Multiple booking platforms on laptop and mobile"
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <h3 className="text-xl md:text-2xl font-bold mb-2">Global Reach</h3>
              <p className="text-sm md:text-base opacity-90">Connect with travelers worldwide through 120+ platforms</p>
            </div>
          </div>
        </motion.div>

        {/* Platforms Marquee */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          viewport={{ once: true }}
          className="relative overflow-hidden"
        >
          {(() => {
            const platforms = [
              { name: 'Airbnb', logo: '/images/18bricksholidayhomepartnerlogo/airbnb-1.png' },
              { name: 'Booking.com', logo: '/images/18bricksholidayhomepartnerlogo/booking.com_.png' },
              // { name: 'Tripadvisor', logo: '/images/18bricksholidayhomepartnerlogo/TripAdvisor-logo-1.jpg' },
              // { name: 'HomeAway', logo: '/images/18bricksholidayhomepartnerlogo/homeaway-vacation-rental-house-logo-renting-png-favpng-pLiviBXsCGY1s6skbbwMRhQ1s.jpg' },
              { name: 'dubizzle', logo: '/images/18bricksholidayhomepartnerlogo/dubizzle-1.png' },
              // { name: 'Expedia', logo: '/images/18bricksholidayhomepartnerlogo/expoedia-scaled-1.webp' },
              { name: 'bayut', logo: '/images/18bricksholidayhomepartnerlogo/bayut-logo-green.png' },
              // { name: 'agoda', logo: '/images/18bricksholidayhomepartnerlogo/agoda.png' },
              { name: 'Property Finder', logo: '/images/18bricksholidayhomepartnerlogo/pf (1).png' },
              // { name: 'Homes&Villas', logo: '/images/18bricksholidayhomepartnerlogo/Marriott-new-logo.webp' },
              // { name: 'Vrbo', logo: '/images/18bricksholidayhomepartnerlogo/vrbo.png' },
            ];

            return (
              <>
                <div className="marquee-track inline-flex items-center gap-12 py-2">
                  {platforms.concat(platforms).map((platform, index) => (
                    <div
                      key={`${platform.name}-${index}`}
                      className="flex items-center justify-center p-4 w-40 h-40"
                    >
                      <Image src={platform.logo} alt={platform.name} width={100} height={100} className='object-contain w-full h-full' />
                    </div>
                  ))}
                </div>
                <style jsx>{`
                  .marquee-track {
                    animation: marquee 28s linear infinite;
                    will-change: transform;
                  }
                  @keyframes marquee {
                    from { transform: translateX(0); }
                    to { transform: translateX(-50%); }
                  }
                `}</style>
              </>
            );
          })()}
        </motion.div>

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-primary/5 p-6 rounded-lg shadow-sm relative overflow-hidden group border border-primary/20">
              <div className="absolute top-0 right-0 w-20 h-20 opacity-10">
                <Image
                  src="https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg"
                  alt="Global reach"
                  fill
                  className="object-cover rounded-full"
                />
              </div>
              <div className="text-4xl mb-4">📈</div>
              <h3 className="text-xl font-semibold text-primary mb-2">
                Maximum Exposure
              </h3>
              <p className="text-gray-600">
                Reach millions of travelers across 120+ platforms worldwide
              </p>
            </div>
            <div className="bg-secondary/5 p-6 rounded-lg shadow-sm relative overflow-hidden group border border-secondary/20">
              <div className="absolute top-0 right-0 w-20 h-20 opacity-10">
                <Image
                  src="https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg"
                  alt="Automation"
                  fill
                  className="object-cover rounded-full"
                />
              </div>
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-semibold text-secondary mb-2">
                Automated Management
              </h3>
              <p className="text-gray-600">
                We handle all platform updates, pricing, and availability automatically
              </p>
            </div>
            <div className="bg-primary/5 p-6 rounded-lg shadow-sm relative overflow-hidden group border border-primary/20">
              <div className="absolute top-0 right-0 w-20 h-20 opacity-10">
                <Image
                  src="https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg"
                  alt="Revenue growth"
                  fill
                  className="object-cover rounded-full"
                />
              </div>
              <div className="text-4xl mb-4">💰</div>
              <h3 className="text-xl font-semibold text-primary mb-2">
                Higher Revenue
              </h3>
              <p className="text-gray-600">
                Optimize pricing and maximize bookings across all channels
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PlatformsSection;
